import { CameraSelector } from './CameraSelector'

import { Camera } from '@babylonjs/core/Cameras/camera'
import '@babylonjs/core/Materials/Textures/Loaders/ddsTextureLoader'
import '@babylonjs/core/Materials/Textures/Loaders/envTextureLoader'
import { CubeTexture } from '@babylonjs/core/Materials/Textures/cubeTexture'
import { Color4 } from '@babylonjs/core/Maths/math.color'
import { Matrix } from '@babylonjs/core/Maths/math.vector'

import { BaseViewer } from './BaseViewer'
import { CameraAngle } from './Cameras'
import { ConfigurationIntepreter, GetTempleInfo } from './ConfigurationInterpreter'
import { ContextMenu } from './ContextMenu'
import { Frame, FrameConfiguration, GetConfigurationString } from './Frame'
import { LoadingIndicator } from './LoadingIndicator'
import { ModelViewerSettings } from './ModelViewerSettings'
import { GetUrl, Settings } from './Settings'

import { name, version } from './Version'

import { ImageProcessingConfiguration } from '@babylonjs/core/Materials/imageProcessingConfiguration'
import { FxaaPostProcess } from '@babylonjs/core/PostProcesses/fxaaPostProcess'
import { TAARenderingPipeline } from '@babylonjs/core/PostProcesses/RenderPipeline/Pipelines/taaRenderingPipeline'
import { Scene } from '@babylonjs/core/scene'
import { BetaIndicator } from './BetaIndicator'
import Ground from './Effects/Ground'
import { GetDefaultMaterial } from './MaterialLoader'
import { ConfigureTelemetry, TrackEvent, TrackException } from './Telemetry'

export type Theme = 'Titanium' | 'Precious'

export default class ModelViewer extends BaseViewer {
	public frame = new Frame(this.scene)
	private thumbnails = new CameraSelector(this)

	private contextMenu = new ContextMenu(this)

	private _view: string
	private _cameraAngle: CameraAngle

	public configuration: FrameConfiguration
	// public pipeline = new RenderingPipeline(this.scene, [this.camera, this.thumbnails.camera])
	public taa = new TAARenderingPipeline('TAA', this.scene)
	// public msaa = new PassPostProcess("MSAA", 2, this.camera)
	public fxaa = new FxaaPostProcess('FXAA', 1, this.camera)

	public ground: Ground

	// public allowErrors = false

	private isLoaded = false

	public settings = new ModelViewerSettings(this)

	loadingIndicator = new LoadingIndicator(this)

	constructor() {
		console.info(`%c ${name} ${version}${(import.meta.env.PROD ? '-' + import.meta.env.VITE_BUILDID : '')}`, `background: #101443; color: #FFFFFF; font-size: 1.2em; padding: 4px 10px; border-radius: 1.2em`)
		super()
		Scene.DefaultMaterialFactory = GetDefaultMaterial

		if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) this.settings.transitions = [] // Lisbeth-mode

		this.scene.imageProcessingConfiguration.isEnabled = true
		this.scene.imageProcessingConfiguration.toneMappingType = ImageProcessingConfiguration.TONEMAPPING_ACES
		this.scene.imageProcessingConfiguration.toneMappingEnabled = true

		this.fxaa.samples = 4

		ConfigureTelemetry()

		if (window.location.host.includes('customiser')) {
			new BetaIndicator(this)
		}
	}

	connectedCallback() {
		super.connectedCallback()
		this.updateBackgroundColor(this.settings.backgroundColor)
	}

	updateBackgroundColor(color: string) {
		// this.scene.clearColor = Color4.FromHexString(color)
		this.scene.clearColor = new Color4(0, 0, 0, 0)
	}

	static get observedAttributes() {
		return ['theme', 'config']
	}

	public get view(): string {
		return this._view
	}

	public showScale() {
		document.body.append(document.createElement('realtime-scale'))
	}

	public set view(value: string) {
		this._view = value

		if (['Model', 'ModelSize', 'Color/FrontColor', 'Color/UpperRimColor', 'Color/LowerRimColor', 'Color/GrooveColor', 'Color/InnerRimColor', 'BridgeDesignSizeClipLength', 'Color/BridgeColor', 'Color/PreciousMetal', 'NosepadDesign'].includes(this.view)) {
			this.cameraAngle = CameraAngle.Front
		} else if (['FinishHead/FinishNosepad', 'FinishHead/FinishBridge'].includes(this.view)) {
			this.cameraAngle = CameraAngle.Perspective
		} else if (['Temple/TempleDesignByFinish'].includes(this.view) && this.configuration.productline == 'RIM') {
			this.cameraAngle = CameraAngle.Top
		} else if (['Temple/TempleDesignByFinish', 'Temple/TempleLength', 'Color/TempleColor', 'Temple/TempleDesignGrouped'].includes(this.view)) {
			this.cameraAngle = CameraAngle.Side
		} else if (['Temple/GemstoneSize', 'Nosepad/GemstoneSize', 'Color/GemstoneColor'].includes(this.view)) {
			this.cameraAngle = CameraAngle.Diamond
		} else if (['FinishTemple', 'FinishHead', 'FinishHead/FinishTemple', 'FinishHead/FinishFront'].includes(this.view)) {
			this.cameraAngle = CameraAngle.Perspective
		} else if (['Engraving'].includes(this.view)) {
			this.cameraAngle = CameraAngle.Engraving
		} else {
			console.log('Move camera?', this.view)
		}
	}

	attributeChangedCallback(name, oldValue, newValue) {
		if (name == 'config' && oldValue != newValue && this.config != newValue) this.config = newValue
		if (name == 'theme') this.settings.theme = newValue
	}

	public set theme(value: Theme) {
		this.settings.theme = value
	}

	public get theme(): Theme {
		return this.settings.theme
	}

	public set config(value: string) {
		const params = new URLSearchParams(value)
		if (!params.get('MODEL')) return
		const properties = Array.from(params).filter(x => x[0] != x[0].toUpperCase())
		const components = Array.from(params).filter(x => x[0] == x[0].toUpperCase()).map(x => [x[0], Object.fromEntries(x[1].split(' ').map((v, i) => [['design', 'size', 'color'][i], v]))])
		this.configuration = Object.fromEntries([...properties, ['components', Object.fromEntries(components)]]) as FrameConfiguration
		this.configuration.isRimless = params.get('isRimless') == 'true'
		this.setAttribute('config', value)
		if (this.isLoaded) this.load()
	}

	public get config(): string {
		return GetConfigurationString(this.configuration)
	}

	public get configName(): string {
		return this.configuration ? `LINDBERG ${[this.configuration.components.MODEL?.design, this.configuration.components.TEMPLE?.design?.split(';')[0]].join('/')}` : ''
	}

	public get cameraAngle(): CameraAngle {
		return this._cameraAngle
	}
	public set cameraAngle(value: CameraAngle) {
		this._cameraAngle = value
		this.camera.transform(this.cameraAngle, this.settings.transitions.includes('Camera'))
		this.thumbnails.setSelectedCamera(value)
	}

	async load() {
		// this.taa.isEnabled = false
		this.loadingIndicator.show()
		const startTime = performance.now()
		const initialLoad = !this.isLoaded

		/*try/catch to avoid Customiser Errors*/
		try {
			if (this.isLoaded) {
				await this.updateFrame()
			} else {
				this.isLoaded = true
				await this.init()
			}
		} catch (error) {
			console.warn(error)
			TrackException(error, { type: 'LoadFrame', configuration: Settings.baseUrl + '?' + this.config })
			// showError(error.message)
		}
		this.loadingIndicator.hide()
		if (this.taa.isEnabled) {
			this.taa.isEnabled = false
			this.taa.isEnabled = true
		}

		const pageData = { name: initialLoad ? 'Initial' : 'Update', properties: { 'Referer': window.location.origin + window.location.pathname, 'Configuration': Settings.baseUrl + '?' + this.config, duration: performance.now() - startTime } }
		// console.log("TRACK PAGE VIEW", pageData)
		TrackEvent(pageData)
		if (!this.frame.hasErrors) {
			this.dispatchEvent(new CustomEvent('loaded'))
		} else {
			this.dispatchEvent(new CustomEvent('error'))
			console.log('FRAME ERRORS', this.frame.hasErrors)
		}
		// setTimeout(() => this.taa.isEnabled = true, 3000)
	}

	async init() {
		await GetTempleInfo() // TODO: Refactor order

		if (this.configuration) {
			this.frame.configuration = ConfigurationIntepreter(this.configuration)
			this.frame.configuration.allowErrors = this.settings.allowErrors
		}

		const result = await Promise.all([
			this.configuration?.productline ? this.frame.load(false) : null,
			this.loadEnvironment(this.theme)
		])
		// log("init loaded")

		// console.log(result)

		this.showFallback()

		await this.ready()

		await this.thumbnails.update()
		this.thumbnails.addEventListener('change', e => this.camera.transform(this.thumbnails.selected, this.settings.transitions.includes('Camera')))
		this.shadowRoot.append(this.thumbnails)
	}

	async loadEnvironment(environmentName: string) {
		const name = `Environments/${environmentName}.env`
		const hdrTexture = this.scene.getTextureByName(name) || CubeTexture.CreateFromPrefilteredData(GetUrl(name), this.scene)
		hdrTexture.name = `Environments/${environmentName}.env`
		this.scene.environmentTexture = hdrTexture
	}

	async updateFrame() {
		if (!this.configuration) return
		try {
			this.frame.configuration = ConfigurationIntepreter(this.configuration)
			this.frame.configuration.allowErrors = this.settings.allowErrors
		} catch (error) {
			TrackException(error, { type: 'UpdateFrame', configuration: this.configuration })
			this.frame.hasErrors = true
			this.showFallback()
			return
		}

		await this.frame.load(this.settings.transitions.includes('Frame'))
		this.showFallback()

		if (this.settings.transitions.includes('Frame')) {
			this.updateTimeout((Settings.morphDuration * 1000) + 100)
		} else {
			this.updateTimeout(100)
			// this.updateThumbnails()
		}
	}
	override resize() {
		super.resize()
		const frameWidth = this.settings.maxFrameWidth // || this.frame.size.x * 1000
		const frameHeight = this.settings.maxFrameHeight // || this.frame.size.y * 1000

		const pixelPitch = null // getSavedPixelPitch()
		const realWorldFov = Math.atan((this.clientHeight * devicePixelRatio * pixelPitch) / 1000)

		const visibleHeight = Math.max(0, this.clientHeight - this.offset.top - this.offset.bottom)
		const visibleWidth = Math.max(0, this.clientWidth - this.offset.left - this.offset.right)

		const horizontalFov = Math.atan(frameWidth / 1000) * (this.clientHeight / this.clientWidth)
		const verticalFov = Math.atan(frameHeight / 1000)

		const HorizontalVisibleFov = Math.atan(frameWidth * (this.clientWidth / visibleWidth) / 1000) * (this.clientHeight / this.clientWidth)
		const verticalVisibleFov = Math.atan(frameHeight * (this.clientHeight / visibleHeight) / 1000)

		this.camera.fov = Math.max(verticalVisibleFov, HorizontalVisibleFov, realWorldFov)
		this.camera.fovMode = Camera.FOVMODE_VERTICAL_FIXED

		const x = (this.offset.left - this.offset.right) / this.clientWidth
		const y = (this.offset.bottom - this.offset.top) / this.clientHeight
		this.camera.screenOffset = Matrix.Translation(x, y, 0)

		this.camera.getProjectionMatrix(true).multiplyToRef(this.camera.screenOffset, this.camera.getProjectionMatrix())

		if (realWorldFov) {
			console.log('REALWORLD SCALE', (realWorldFov / this.camera.fov * 100).toFixed(2))
		}
	}

	showFallback() {
		this.loadingIndicator.hide()

		this.shadowRoot.querySelector('div.message')?.remove()
		const isBrowserSupported = true // Disabled for now

		this.classList.toggle('error', this.frame.hasErrors)

		if (isBrowserSupported && (!this.frame.hasErrors || this.settings.allowErrors)) {
			this.frame.setEnabled(true)
			this.thumbnails.style.display = null
		} else {
			this.frame.setEnabled(false)
			this.thumbnails.style.display = 'none'

			const message = document.createElement('div')
			message.className = 'message'
			this.shadowRoot.append(message)

			const h1 = document.createElement('h1')
			h1.className = this.settings.theme
			h1.innerText = 'no image available'
			message.append(h1)

			if (!isBrowserSupported) {
				const h2 = document.createElement('h2')
				h2.className = this.settings.theme
				h2.innerText = `The LINDBERG Realtime Viewer supports the latest versions of the following browsers: Chrome, Firefox, Safari and Edge`
				message.append(h2)
			}
		}
	}

	timeout: number
	async updateTimeout(time: number) {
		clearTimeout(this.timeout)
		await until(() => {
			return !this.camera.isMoving
		})
		this.timeout = window.setTimeout(this.updateThumbnails.bind(this), time)
	}

	async updateThumbnails() {
		if (this.isLoaded) this.thumbnails?.update()
	}
}

const until = (predFn: Function): Promise<void> => {
	const poll = (done: Function) => (predFn() ? done() : setTimeout(() => poll(done), 100))
	return new Promise(poll)
}

customElements.define('realtime-modelviewer', ModelViewer)
